import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../components/auth/AuthLayout';
import { AuthCard } from '../components/auth/AuthCard';
import { Button } from '../components/shared/Button';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export const AuthCallbackPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const handleCallback = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : '');
      const accessToken = params.get('accessToken');
      const refreshToken = params.get('refreshToken');

      if (!accessToken || !refreshToken) {
        setError(t('auth.oauth.callback.error'));
        return;
      }

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      // Clear the fragment so tokens are not left in the URL
      window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search,
      );

      try {
        await refreshUser();
        navigate('/dashboard', { replace: true });
      } catch {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setError(t('auth.oauth.callback.error'));
      }
    };

    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run exactly once on mount
  }, []);

  return (
    <AuthLayout
      variant="login"
      title={t('auth.oauth.callback.title')}
      subtitle={t('auth.oauth.callback.subtitle')}
    >
      <AuthCard
        label={t('auth.oauth.callback.title')}
        title={t('auth.oauth.callback.title')}
        subtitle={t('auth.oauth.callback.subtitle')}
      >
        {error ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-2xl border border-error/25 bg-error/10 p-4 text-error">
              <AlertCircle className="w-6 h-6 flex-shrink-0" />
              <span>{error}</span>
            </div>
            <Link to="/login">
              <Button variant="primary" className="w-full">
                {t('auth.oauth.callback.backToLogin')}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
            <p className="text-slate-600">{t('auth.oauth.callback.loading')}</p>
          </div>
        )}
      </AuthCard>
    </AuthLayout>
  );
};

export default AuthCallbackPage;
